import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarDays, Clock, Loader2, CheckCircle2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "w-full rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3.5 font-body text-sm text-white placeholder:text-neutral-500 outline-none transition-colors duration-300 focus:border-[#ffb000]/70 focus:bg-white/[0.06]";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const buildDays = () => {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < 12) {
    if (d.getDay() !== 0) days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
};

const DAY_FMT = new Intl.DateTimeFormat("en-GB", { weekday: "short" });
const DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });

export const Booking = () => {
  const days = useMemo(buildDays, []);
  const [date, setDate] = useState(iso(days[0]));
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", vehicle: "", service: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let live = true;
    setSlotsLoading(true);
    setTime("");
    axios
      .get(`${API}/bookings/availability`, { params: { date } })
      .then(({ data }) => live && setSlots(data.slots))
      .catch(() => live && setSlots([]))
      .finally(() => live && setSlotsLoading(false));
    return () => {
      live = false;
    };
  }, [date, done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!time) {
      toast.error("Pick a time slot first.");
      return;
    }
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, date, time });
      setDone(data);
      setForm({ name: "", phone: "", vehicle: "", service: "", notes: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please ring us directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" data-testid="booking" className="relative px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-14 flex flex-col gap-4 md:mb-16 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <span className="font-accent text-xs uppercase tracking-[0.4em] text-[#ffb000]">Book Luke</span>
            <h2 className="mt-4 font-display text-4xl font-700 uppercase leading-none chrome-text md:text-6xl">
              Pick Your Slot
            </h2>
          </div>
          <p className="max-w-sm font-body text-sm leading-relaxed text-neutral-400">
            Choose a day and time that suits you — Mon to Sat, 8am till 6pm. Luke will ring you to confirm the
            details before anything's set in stone.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="chrome-inset rounded-3xl border border-white/12 bg-white/[0.04] p-6 backdrop-blur-2xl md:p-10"
        >
          {done ? (
            <div data-testid="booking-success" className="flex min-h-[400px] flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-16 w-16 text-[#ffb000]" strokeWidth={1.4} />
              <h3 className="mt-6 font-accent text-2xl uppercase tracking-wide text-white">Slot Requested</h3>
              <p className="mt-3 max-w-md font-body text-sm leading-relaxed text-neutral-400">
                Nice one — your request for <span className="text-white">{DATE_FMT.format(new Date(done.date))} at {done.time}</span> is
                in (ref <span className="text-[#ffb000]">{done.ref}</span>). {CONTACT.owner} will give you a quick ring to
                confirm everything. Nothing's charged until you've spoken.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href={CONTACT.phoneHref} className="flex items-center justify-center gap-2 rounded-full bg-[#ffb000] px-6 py-3 font-accent text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-[#ff8c00]">
                  <PhoneCall className="h-4 w-4" /> Or Ring {CONTACT.owner} Now
                </a>
                <button data-testid="booking-reset" onClick={() => setDone(null)} className="rounded-full border border-white/20 px-6 py-3 font-accent text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-white/5">
                  Book Another Slot
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 gap-10 lg:grid-cols-2">
              <div>
                <div className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.25em] text-neutral-400">
                  <CalendarDays className="h-4 w-4 text-[#ffb000]" /> 1 · Pick a day
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-6">
                  {days.map((d) => {
                    const v = iso(d);
                    const active = v === date;
                    return (
                      <button
                        key={v}
                        type="button"
                        data-testid={`booking-day-${v}`}
                        onClick={() => setDate(v)}
                        className={`rounded-xl border px-2 py-3 text-center transition-colors duration-300 ${
                          active
                            ? "border-[#ffb000] bg-[#ffb000]/15 text-white"
                            : "border-white/10 bg-white/[0.03] text-neutral-400 hover:border-white/30"
                        }`}
                      >
                        <div className="font-accent text-[10px] uppercase tracking-[0.2em]">{DAY_FMT.format(d)}</div>
                        <div className={`mt-1 font-display text-sm ${active ? "text-[#ffb000]" : ""}`}>{DATE_FMT.format(d)}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-8 flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.25em] text-neutral-400">
                  <Clock className="h-4 w-4 text-[#ffb000]" /> 2 · Pick a time
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="booking-slots">
                  {slotsLoading ? (
                    <div className="col-span-full flex items-center gap-2 py-6 font-body text-sm text-neutral-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Checking availability…
                    </div>
                  ) : (
                    slots.map((s) => {
                      const active = s.time === time;
                      return (
                        <button
                          key={s.time}
                          type="button"
                          disabled={!s.available}
                          data-testid={`booking-slot-${s.time}`}
                          onClick={() => setTime(s.time)}
                          className={`rounded-xl border px-2 py-3 font-accent text-sm tracking-wide transition-colors duration-300 ${
                            active
                              ? "border-[#ffb000] bg-[#ffb000] text-black"
                              : s.available
                              ? "border-white/10 bg-white/[0.03] text-neutral-300 hover:border-[#ffb000]/50"
                              : "cursor-not-allowed border-white/5 bg-white/[0.01] text-neutral-700 line-through"
                          }`}
                        >
                          {s.time}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <div className="font-accent text-[11px] uppercase tracking-[0.25em] text-neutral-400">3 · Your details</div>
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input data-testid="booking-name" className={FIELD} placeholder="Your name *" value={form.name} onChange={set("name")} />
                    <input data-testid="booking-phone" className={FIELD} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
                  </div>
                  <input data-testid="booking-vehicle" className={FIELD} placeholder="Vehicle (make / model)" value={form.vehicle} onChange={set("vehicle")} />
                  <select data-testid="booking-service" className={`${FIELD} appearance-none`} value={form.service} onChange={set("service")}>
                    <option value="" className="bg-black">What needs doing?</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title} className="bg-black">{s.title}</option>
                    ))}
                    <option value="Other" className="bg-black">Other / Not sure</option>
                  </select>
                  <textarea data-testid="booking-notes" rows={3} className={`${FIELD} resize-none`} placeholder="Anything else Luke should know? (optional)" value={form.notes} onChange={set("notes")} />
                  <button
                    data-testid="booking-submit"
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-[#ffb000] px-8 py-4 font-accent text-sm font-600 uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#ff8c00] disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {loading ? "Requesting…" : time ? `Request ${time} Slot` : "Request My Slot"}
                  </button>
                  <p className="text-center font-body text-xs text-neutral-500">
                    No payment taken. {CONTACT.owner} rings you to confirm — cancel or change any time.
                  </p>
                </div>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
